import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Grid from '@mui/material/Grid';
import BasicCreativeFields from './../BasicCreativeFields';
import { FC, useEffect, useRef, useCallback } from 'react';
import { PlatformType } from '../../../../constants/available-platforms';
import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import PhotoLibrary from '@mui/icons-material/PhotoLibrary';
import { ISingleCreativeFormValuesModel } from '@/editor/shared/components/forms/creatives/models/single-creative-form-values.model';
import { FormikHelpers } from 'formik';
import { useAppDispatch, useAppSelector } from '@/store/hooks/redux';
import useFormSubmission from '@/editor/shared/hooks/formSubmission';
import { setcreativeSubmissionProgress } from '@/editor/store/reducers/creative-step.slice';

interface IGoogleCreativeFormProps {
  platform: PlatformType;
  initialValues: ISingleCreativeFormValuesModel;
  onSubmit?: (
    values: ISingleCreativeFormValuesModel,
    formikHelpers: FormikHelpers<ISingleCreativeFormValuesModel>
  ) => void;
  onValuesChange?: (values: ISingleCreativeFormValuesModel) => void;
  onMediaLibraryOpen?: (platform: PlatformType) => void;
  validationSchema?: any;
  areFieldsRequired?: boolean;
  showMediaLibraryButton?: boolean;
  autoSave?: boolean;
  updating?: boolean;
}

const GoogleCreativeForm: FC<IGoogleCreativeFormProps> = ({
  platform,
  initialValues,
  onSubmit = async () => {},
  onValuesChange,
  onMediaLibraryOpen,
  validationSchema,
  areFieldsRequired = true,
  showMediaLibraryButton = true,
  autoSave = false,
  updating = false,
}) => {
  const formSubmissionProgress = useAppSelector((state) => state.creativeStepSliceReducer.creativeSubmissionProgress);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: onSubmit,
  });

  const formikRef = useRef(formik);

  const { handleSubmit } = useFormSubmission({ formik, formSubmissionProgress, updating, platform });

  const handleValuesChange = useCallback(
    (values: ISingleCreativeFormValuesModel) => {
      if (onValuesChange) {
        onValuesChange(values);
      }
    },
    [onValuesChange]
  );

  useEffect(() => {
    handleValuesChange(formik.values);
  }, [formik.values, handleValuesChange]);

  const handleMediaLibraryOpen = useCallback(() => {
    if (onMediaLibraryOpen) {
      onMediaLibraryOpen(platform);
    }
  }, [onMediaLibraryOpen, platform]);

  const handleUpdate = () => {
    dispatch(setcreativeSubmissionProgress({ active: true, update: true, platform: platform }));
  };

  useEffect(() => {
    handleSubmit();
  }, [formSubmissionProgress.active, handleSubmit]);

  return (
    <FormikProvider value={formik}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <form noValidate onSubmit={formik.handleSubmit}>
            <BasicCreativeFields areFieldsRequired={areFieldsRequired} platform={platform} />
          </form>
        </Grid>

        {showMediaLibraryButton && (
          <Grid item xs={12} sx={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between' }}>
            <ButtonGroup variant="contained" color="secondary" size="small">
              <Button startIcon={<PhotoLibrary />} disableElevation onClick={handleMediaLibraryOpen}>
                {t('mediaLibrary')}
              </Button>
            </ButtonGroup>
          </Grid>
        )}
      </Grid>
    </FormikProvider>
  );
};

export default GoogleCreativeForm;
